import { Accordion, Message, Modal } from "semantic-ui-react";

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import db from "astrid-firebase/src/db";
import sessionStatuses from "astrid-firestore/src/api/sessions/constants/sessionStatuses";
import { canGrabSession } from "astrid-firestore/src/api/sessions/grabSession";
import { useDocumentData } from "astrid-firestore/src/hooks";

import Button from "../../../ui/components/Buttons/Button";
import PrimaryButton from "../../../ui/components/Buttons/PrimaryButton";
import LoadingContext from "../../../ui/components/LoadingContext/LoadingContext";

import { useProfile } from "../../../authentication/state/profile";
import useHasRight from "../../../authorization/hooks/useHasRight";

import CalendarCancelSessionForm from "./components/CalendarCancelSessionForm";
import CalendarEditSession from "./components/CalendarEditSession";
import CalendarGrabSessionButton from "./components/CalendarGrabSessionButton";
import CalendarSessionInformation from "./components/CalendarSessionInformation";
import CalendarSessionProductionInformation from "./components/CalendarSessionProductionInformation";

export default function CalendarSessionModal({ session, onClose, sessions, setCreateSessionData }) {
	const { t } = useTranslation();
	const user = useProfile();
	const hasRight = useHasRight();

	const [isCancelling, setIsCancelling] = useState(false);
	const [error, setError] = useState(null);

	const isSessionRecorder = user.id === session.recorder;
	const isSessionReader = session.reader.includes(user.id);
	const isSessionManager = session.managerIds?.includes(user.id);

	const today = new Date();
	today.setHours(0, 0, 0, 0);

	const hasSessionEnded = session.end.toDate() < today;

	const canEditSessions =
		hasRight("calendar.editAllSessions") ||
		(hasRight("calendar.editOwnSessions") && session.managerIds?.includes(user.id));

	const [production, loadingProduction, errorProduction] = useDocumentData(
		session?.production && db.collection("productions").doc(session.production),
	);

	const onClickBack = () => {
		setIsCancelling(false);
	};

	const onClickBookAgain = async () => {
		await setCreateSessionData({
			agreement: session.agreementRef,
			polish: session.polish,
			price: session.priceRef,
			priceId: session.priceId,
			productionId: session.production,
			reader: session.reader,
			recorder: session.recorder,
		});

		onClose();
	};

	return (
		<Modal size="small" open closeOnDimmerClick onClose={onClose}>
			<LoadingContext loading={loadingProduction} error={errorProduction}>
				<Modal.Header>{`${t("session", "Session")} - ${production?.title}`}</Modal.Header>

				{!isCancelling ? (
					<>
						<Modal.Content>
							{session.status === sessionStatuses.CANCELLED && (
								<Message error>
									{t("sessionHasBeenCancelled", "This session has been cancelled")}
								</Message>
							)}

							<CalendarSessionInformation session={session} production={production} />

							<Accordion fluid>
								{canEditSessions &&
									!hasSessionEnded &&
									session.status !== sessionStatuses.CANCELLED && (
										<CalendarEditSession
											session={session}
											production={production}
											sessions={sessions}
										/>
									)}

								<CalendarSessionProductionInformation session={session} production={production} />
							</Accordion>

							{production?.deleted && (
								<Message negative>{t("prodIsDeleted", "This production is deleted")}</Message>
							)}

							{error && <Message negative>{t("errorMessageHeader", "Something went wrong")}</Message>}
						</Modal.Content>

						<Modal.Actions>
							<Button color={undefined} onClick={onClose}>
								{t("close")}
							</Button>

							{canEditSessions && !hasSessionEnded && session.status !== sessionStatuses.CANCELLED && (
								<PrimaryButton color="red" onClick={() => setIsCancelling(true)}>
									{t("cancelSession", "Cancel session")}
								</PrimaryButton>
							)}

							{hasRight("sessions.book") && (
								<PrimaryButton color={undefined} onClick={onClickBookAgain}>
									{t("bookAgain", "Book again")}
								</PrimaryButton>
							)}

							{(canEditSessions || isSessionRecorder || isSessionManager || isSessionReader) &&
								!production?.deleted && (
									<PrimaryButton as={Link} to={"/production/" + session.production}>
										{t("toProduction", "To production")}
									</PrimaryButton>
								)}

							{!production?.deleted &&
								hasRight("studioSession.connect") &&
								canGrabSession({ session, user, production }) && (
									<CalendarGrabSessionButton
										session={session}
										production={production}
										setError={setError}
									/>
								)}
						</Modal.Actions>
					</>
				) : (
					<CalendarCancelSessionForm onClickBack={onClickBack} onClose={onClose} session={session} />
				)}
			</LoadingContext>
		</Modal>
	);
}
